import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Modal, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const Brands = () => {
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [editId, setEditId] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post("/brand_list.php", {});
      
      if (response.data.head.code === 200) {
        setBrands(response.data.body);
      } else {
        setError(response.data.head.msg || "Failed to fetch brands");
      }
    } catch (err) {
      console.error("Error fetching brands:", err);
      setError("Failed to load brands. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const handleShow = () => {
    setBrandName("");
    setEditId(null);
    setModalError(null);
    setShow(true);
  };

  const handleClose = () => {
    setBrandName("");
    setEditId(null);
    setModalError(null);
    setShow(false);
  };

  const handleEdit = (brand) => {
    setBrandName(brand.name);
    setEditId(brand.id);
    setModalError(null);
    setShow(true);
  };

  const handleSave = async () => {
    if (!brandName.trim()) {
      setModalError("Brand name is required");
      return;
    }

    setModalLoading(true);
    setModalError(null);

    try {
      if (editId) {
        // Update existing brand
        const response = await api.post("/brand_edit.php", {
          id: editId,
          name: brandName
        });
        
        if (response.data.head.code === 200) {
          // Update in local state
          setBrands(brands.map((brand) =>
            brand.id === editId ? { ...brand, name: brandName } : brand
          ));
          handleClose();
          alert("Brand updated successfully!");
        } else {
          setModalError(response.data.head.msg || "Failed to update brand");
        }
      } else {
        // Add new brand
        const response = await api.post("/brand_add.php", {
          name: brandName
        });
        
        if (response.data.head.code === 200) {
          const newBrand = {
            id: response.data.body.brand_id,
            name: brandName
          };
          setBrands([...brands, newBrand]);
          handleClose();
          alert("Brand added successfully!");
        } else {
          setModalError(response.data.head.msg || "Failed to add brand");
        }
      }
    } catch (err) {
      console.error("Error saving brand:", err);
      setModalError("Failed to save brand. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  // Delete brand handler with API
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) {
      return;
    }

    try {
      const response = await api.post("/brand_delete.php", { id });
      
      if (response.data.head.code === 200) {
        setBrands(brands.filter((brand) => brand.id !== id));
        alert("Brand deleted successfully!");
      } else {
        alert("Failed to delete brand: " + response.data.head.msg);
      }
    } catch (err) {
      console.error("Error deleting brand:", err);
      alert("Failed to delete brand. Please try again.");
    }
  };

  // Filter brands based on search
  const filteredBrands = brands.filter((brand) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      String(brand.id).toLowerCase().includes(query) ||
      (brand.name && brand.name.toLowerCase().includes(query))
    );
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentBrands,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredBrands, 10);

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Brands Management</h2>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading brands...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Brands Management</h2>
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchBrands}>
            Retry
          </button>
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container fluid className="outer-container my-3">
        <h2 className="text-center mb-3">Brands Management</h2>
        
        {/* Search and Add Brand Section */}
        <Row className="mb-3 align-items-center">
          <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
            <Form.Control
              placeholder="Search by brand name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>

          <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end">
            <Button className="btn-top" onClick={handleShow}>
              <FaPlus className="me-2" /> Add Brand
            </Button>
            <button 
              className="btn btn-outline-primary ms-2 d-inline-flex align-items-center"
              onClick={fetchBrands}
              title="Refresh brand list"
            >
              <i className="fas fa-sync-alt me-2"></i> Refresh
            </button>
          </Col>
        </Row>

        {/* Table Section */}
        <div className="table-pagination-wrapper">
          <div className="table-pagination-content">
            <Table bordered responsive className="table-striped customer-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Brand Name</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentBrands.map((brand, index) => (
                  <tr key={brand.id}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{brand.name}</td>
                    <td>
                      <div className="action-icons">
                        <FaEdit
                          className="edit-icon me-2"
                          onClick={() => handleEdit(brand)}
                          title="Edit Brand"
                          style={{ cursor: "pointer" }}
                        />
                        <FaTrash
                          className="delete-icon"
                          title="Delete Brand"
                          onClick={() => handleDelete(brand.id, brand.name)}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {currentBrands.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center">
                      {search ? "No brands found matching your search" : "No brands found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </Container>

      {/* Modal for Add/Edit Brand */}
      <Modal show={show} onHide={handleClose} centered className="department-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Brand" : "Add Brand"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && (
            <div className="alert alert-danger" role="alert">
              {modalError}
            </div>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Brand Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter brand name"
                disabled={modalLoading}
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button 
                className="btn-submit btn" 
                onClick={handleSave}
                disabled={modalLoading || !brandName.trim()}
              >
                {modalLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {editId ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  editId ? "Update" : "Submit"
                )}
              </Button>
              <Button 
                variant="secondary" 
                onClick={handleClose}
                disabled={modalLoading}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Brands;